import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useAgentVisibility } from '@/entities/agent';
import { getCurrentOrganizationId, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { launchAgentWithWidget, startChatSession, useChatStore } from '@/features/chats';
import {
  getSmartStartCards,
  getSpringboardTabByIdMerged,
  SmartStartGridV4,
  ToolGridV4,
  useSmartStartCards,
  useSpringboardTabs,
  type SmartStartCardItem,
} from '@/features/springboard';
import { FEATURED_CARDS, FeaturedCard } from '@/features/tools-hub';
import {
  SectionPageHeader,
  SectionPageHeaderBackButton,
  SectionPageHeaderContent,
  SectionPageHeaderDescription,
  SectionPageHeaderIcon,
  SectionPageHeaderTitle,
} from '@/shared/ui/layout/SectionPageHeader/SectionPageHeader';
import { ListPageBody } from '@/shared/ui/layout/ListPageLayout/ListPageLayout';
import { ROUTES } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';
import { openAdaptationPicker, useCurrentWorkspaceSlug } from '@/shared/hooks';
import { useTranslation } from 'react-i18next';
import { TOOL_BADGE_TINTS } from '@/shared/ui/ToolBadge';

// eslint-disable-next-line i18next/no-literal-string -- tool category id
const SCALE_TAB_ID = 'scale';
const SCALE_FEATURED_CARD = FEATURED_CARDS.find((card) => card.id === 'adaptation');

export const ScalePage = () => {
  const { t } = useTranslation('tools');

  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Back returns to the surface the user came from (Tools, etc.), falling back
  // to Home when the page is reached directly (deep link, refresh, no origin).
  const backTo = (location.state as { from?: string } | null)?.from ?? ROUTES.HOME;

  const workspaceId = useCurrentWorkspaceId();
  const workspaceSlug = useCurrentWorkspaceSlug();
  const organizationId = getCurrentOrganizationId();
  const initializeChat = useChatStore((state) => state.initializeChat);

  const { tabs: springboardTabs } = useSpringboardTabs();

  const selectedTab = useMemo(
    () => getSpringboardTabByIdMerged(springboardTabs, SCALE_TAB_ID),
    [springboardTabs],
  );

  const { data: smartStartCards } = useQuery({
    queryKey: ['smart-start-cards', SCALE_TAB_ID],
    queryFn: () => getSmartStartCards(SCALE_TAB_ID),
    enabled: !!workspaceId,
  });

  const smartStartAgentIds = useMemo(
    () => (smartStartCards ?? []).map((card) => card.input.agentId),
    [smartStartCards],
  );

  const { isAgentEnabled } = useAgentVisibility([...smartStartAgentIds], {
    workspaceId,
    organizationId,
  });

  const { canOpenTool } = usePermissions();

  const smartCardsResponse = useSmartStartCards({
    tabId: SCALE_TAB_ID,
    enabled: !!workspaceId,
    isAgentEnabled,
    canOpenAgent: canOpenTool,
  });

  const launchAgent = useCallback(
    (agentId: string) => {
      launchAgentWithWidget(navigate, agentId, { backTo: location.pathname });
    },
    [navigate, location.pathname],
  );

  // The featured card sits on the Scale page itself, so it cannot navigate anywhere:
  // it launches Adaptation the same way the Adaptation tool card below it does.
  const openAdaptation = useCallback(() => {
    openAdaptationPicker();
  }, []);

  const startSession = useCallback(
    async (item: SmartStartCardItem) => {
      await startChatSession(initializeChat, navigate, {
        agentId: item.input.agentId,
        prompt: item.input.prompt || '',
        inputs: item.input.options,
        visibilityScope: { workspaceId, organizationId },
        backTo: location.pathname,
      });
    },
    [initializeChat, navigate, workspaceId, organizationId, location.pathname],
  );

  if (!selectedTab) {
    return null;
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div ref={containerRef} className="flex flex-col w-full gap-4 p-4">
        <StaggerItem index={0}>
          <SectionPageHeader>
            <SectionPageHeaderBackButton
              onClick={() => void navigate(backTo)}
              aria-label={t('toolsHub.scalePage.back')}
            />
            <SectionPageHeaderIcon surfaceClassName={TOOL_BADGE_TINTS[selectedTab.id]}>
              <selectedTab.Icon className="size-5" />
            </SectionPageHeaderIcon>
            <SectionPageHeaderContent>
              <SectionPageHeaderTitle>{selectedTab.label}</SectionPageHeaderTitle>
              <StaggerItem index={1} variant="fade">
                <SectionPageHeaderDescription>{selectedTab.subtitle}</SectionPageHeaderDescription>
              </StaggerItem>
            </SectionPageHeaderContent>
          </SectionPageHeader>
        </StaggerItem>

        {SCALE_FEATURED_CARD && (
          <StaggerItem index={2}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FeaturedCard
                id={SCALE_FEATURED_CARD.id}
                title={SCALE_FEATURED_CARD.title}
                description={SCALE_FEATURED_CARD.description}
                poster={SCALE_FEATURED_CARD.poster}
                video={SCALE_FEATURED_CARD.video}
                category={SCALE_FEATURED_CARD.category}
                badgeIcon={SCALE_FEATURED_CARD.badgeIcon}
                linkTo={ROUTES.TOOLS_SCALE}
                onClick={openAdaptation}
                index={0}
              />
            </div>
          </StaggerItem>
        )}

        <StaggerItem index={3}>
          <ListPageBody className="gap-6 p-6">
            <h3 className="text-lg font-medium">{t('toolsHub.scalePage.allTools')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <ToolGridV4
                selectedTab={selectedTab}
                showFlowDigest={false}
                workspaceSlug={workspaceSlug}
                onToolClick={launchAgent}
                className="contents"
              />
            </div>

            {!!smartCardsResponse.cards.length && (
              <>
                <h3 className="text-lg font-medium">{t('toolsHub.scalePage.smartStarts')}</h3>
                <SmartStartGridV4
                  smartCardsResponse={smartCardsResponse}
                  tabId={selectedTab.id}
                  onCardClick={(item) => void startSession(item)}
                />
              </>
            )}
          </ListPageBody>
        </StaggerItem>

        {/* MFINS-2828: GuidedLearningWorkflow temporarily hidden */}
        {/* <GuidedLearningWorkflow category="scale" /> */}
      </div>
    </div>
  );
};
